// vim: set ts=2 sts=2 sw=2 et:

import React from "react";

import BroadcastHeader from "./BroadcastHeader";
import FitLine from "../../live/FitLine";
import FitRow from "./FitRow";
import type { WrpfSponsor } from "../../handoff/types";
import { AttemptCells, attemptCellCount, bestLabel, RecordTag, recordStyle, totalLabel } from "./overlayParts";
import { LIFT_LABELS } from "./overlayView";
import type { FichaView } from "./fichaView";
import PhotoFrame, { PhotoFrameVariant } from "./PhotoFrame";

import styles from "./AthleteCardView.module.scss";

interface Props {
  tournamentName: string;
  sessionLabel: string;
  sponsors: ReadonlyArray<WrpfSponsor>;
  athlete: FichaView | null;
  waitingForNext: boolean;
  photoFrame?: PhotoFrameVariant;
  /** Lado de la foto en px. Las fotos guardadas hoy son de 250×250; al mejorarlas se puede agrandar. */
  photoSize?: number;
}

// Ficha del atleta con la misma piel que el overlay: foto enmarcada sobre un panel de cuero cortado a 15,5°,
// datos a la derecha, el peso en barra en cuero rojo y lo levantado en el torneo con el total dorado.
const AthleteCardView = ({
  tournamentName,
  sessionLabel,
  sponsors,
  athlete,
  waitingForNext,
  photoFrame = "medalla",
  photoSize = 420,
}: Props) => (
  <div className={styles.card} aria-label="Ficha del atleta">
    <BroadcastHeader tournamentName={tournamentName} sessionLabel={sessionLabel} sponsors={sponsors} />

    <div className={styles.photo}>
      {/* La entrada va en un contenedor interno: animar el clip-path del panel borraría su corte en diagonal. */}
      <div key={athlete?.entryKey ?? "espera"} className={styles.photoInner}>
        <PhotoFrame
          url={athlete?.photoUrl ?? null}
          name={athlete?.name ?? "WRPF Argentina"}
          variant={photoFrame}
          size={photoSize}
        />
      </div>
    </div>
    <span className={styles.photoSlash} aria-hidden="true" />
    <span className={styles.photoLine} aria-hidden="true" />

    {athlete === null ? (
      <section className={styles.content}>
        <h1 className={styles.waiting}>{waitingForNext ? "Esperando al siguiente atleta" : "Esperando atleta"}</h1>
      </section>
    ) : (
      <section key={athlete.entryKey} className={styles.content} aria-label="Datos del atleta">
        <div className={styles.identity}>
          <FitLine
            className={styles.name}
            maxFontSize={124}
            singleLineMinFontSize={93}
            wrapMaxFontSize={72}
            minFontSize={52}
            maxLines={2}
          >
            {athlete.name}
          </FitLine>
          {athlete.modality !== null ? <p className={styles.modality}>{athlete.modality}</p> : null}
          <FitRow
            className={styles.origin}
            ariaLabel="Procedencia"
            items={athlete.origin}
            maxFontSize={28}
            minFontSize={18}
          />
        </div>

        {athlete.facts.length > 0 ? (
          <dl className={styles.facts}>
            {athlete.facts.map((fact) => (
              <div key={fact.label} className={styles.fact}>
                <dt>{fact.label}</dt>
                <dd>{fact.value}</dd>
              </div>
            ))}
          </dl>
        ) : null}

        <div className={styles.onBarGroup}>
          {athlete.record !== null ? (
            <div className={styles.recordBand} style={recordStyle(athlete.record.color, athlete.record.textColor)}>
              Intento de {athlete.record.label.toLowerCase()}
            </div>
          ) : null}
          <div
            className={styles.onBar}
            data-record={athlete.record === null ? undefined : "true"}
            style={recordStyle(athlete.record?.color, athlete.record?.textColor)}
          >
            <span>
              {athlete.liftLabel}, {athlete.attemptLabel}
            </span>
            <strong>
              {athlete.weightLabel}
              <small>kg</small>
            </strong>
          </div>
        </div>

        <div className={styles.meet}>
          <h2>En este torneo</h2>
          <div
            className={styles.progress}
            data-attempts={Math.max(3, ...athlete.progress.rows.filter((row) => row.current).map(attemptCellCount))}
          >
            <div className={styles.lifts}>
              {athlete.progress.rows.map((row) => (
                <div key={row.lift} className={styles.liftRow} data-current={row.current ? "true" : undefined}>
                  <span className={styles.liftLabel}>{LIFT_LABELS[row.lift]}</span>
                  {row.current ? (
                    <AttemptCells row={row} attemptNumber={athlete.attemptNumber} className={styles.cells} />
                  ) : (
                    <span className={styles.best} data-state={row.state}>
                      {bestLabel(row)}
                      <RecordTag row={row} className={styles.recordTag} />
                    </span>
                  )}
                </div>
              ))}
            </div>
            {athlete.progress.showTotal ? (
              <div className={styles.total} data-bombed={athlete.progress.bombed ? "true" : undefined}>
                <span>{athlete.progress.bombed ? "Sin total" : "Total"}</span>
                <strong>{totalLabel(athlete.progress)}</strong>
              </div>
            ) : null}
          </div>
        </div>

        {athlete.personalBests.length > 0 ? (
          <div className={styles.personalBests}>
            <h2>Marcas previas</h2>
            <ul>
              {athlete.personalBests.map((best) => (
                <li key={best.label}>
                  {best.label} <strong>{best.value}</strong>
                </li>
              ))}
            </ul>
          </div>
        ) : null}
      </section>
    )}
  </div>
);

export default AthleteCardView;
